import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import { getActiveDepartments } from '../../services/departmentService';
import { Department } from '../../types/department';
import UserMenu from '../common/UserMenu';
import styles from './Header.module.css';

interface HeaderProps {
  onMenuToggle?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const {
    user,
    logout,
    activeDepartment,
    activeDepartmentName,
    setActiveDepartment,
    userDepartments,
    isSystemAdmin,
    viewAllDepartments,
    setViewAllDepartments,
  } = useAuth();
  const [deptOpen, setDeptOpen] = useState(false);
  const [allDepts, setAllDepts] = useState<Department[]>([]);
  const deptRef = useRef<HTMLDivElement>(null);

  const userSlugs = useMemo(() => userDepartments(), [userDepartments]);
  const showDeptSelect = userSlugs.length > 1 || (isSystemAdmin() && viewAllDepartments);

  // Solo necesitamos la lista completa de deptos para el dropdown.
  // Para el caso length === 1, el nombre viene directo del store
  // (activeDepartmentName) sin fetch.
  useEffect(() => {
    if (!showDeptSelect) return;
    getActiveDepartments().then(r => {
      setAllDepts(viewAllDepartments ? r : r.filter((d) => userSlugs.includes(d.slug)));
    }).catch(() => {});
  }, [showDeptSelect, viewAllDepartments, userSlugs]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (deptRef.current && !deptRef.current.contains(e.target as Node)) setDeptOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDeptOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <header className={styles['topbar']} data-testid="header">
      <div className={styles['topbar__left']}>
        <button
          className={styles['topbar__menu-btn']}
          type="button"
          onClick={onMenuToggle}
          aria-label="Abrir menu"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>

        {showDeptSelect ? (
          <div className={styles['topbar__dept-wrapper']} ref={deptRef} data-testid="dept-select">
            <button
              type="button"
              className={styles['topbar__dept-trigger']}
              onClick={() => setDeptOpen(!deptOpen)}
              aria-haspopup="listbox"
              aria-expanded={deptOpen}
              aria-controls="department-options"
            >
              <span className={styles['topbar__page-title']}>{activeDepartmentName || 'Seleccionar'}</span>
              <span className={styles['topbar__dept-arrow']} aria-hidden="true">&#9662;</span>
            </button>
            {deptOpen && (
              <div id="department-options" role="listbox" className={styles['topbar__dept-dropdown']} onClick={e => e.stopPropagation()}>
                {allDepts.map(d => (
                  <button key={d.id}
                    type="button"
                    role="option"
                    aria-selected={d.slug === activeDepartment}
                    className={`${styles['topbar__dept-option']} ${d.slug === activeDepartment ? styles['topbar__dept-option--active'] : ''}`}
                    onClick={() => { setActiveDepartment(d.slug); setDeptOpen(false); }}>
                    {d.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : activeDepartmentName ? (
          <span className={styles['topbar__page-title']} data-testid="active-dept-name">{activeDepartmentName}</span>
        ) : (
          <span className={styles['topbar__page-title']}>Wiki</span>
        )}
      </div>

       <UserMenu
         user={user}
         isSystemAdmin={isSystemAdmin()}
         viewAllDepartments={viewAllDepartments}
         onViewAllToggle={setViewAllDepartments}
         onSignOut={() => { logout(); }}
       />
    </header>
  );
};
